import { dateFullRu } from '@outsource/shared/ru';
import { forwardRef, useRef, useState } from 'react';
import type { InputHTMLAttributes, KeyboardEvent, MouseEvent } from 'react';
import { validDate } from './calendar';
import { DatePopover } from './DatePopover';

/** Any touch input, even with a trackpad attached (an iPad with a keyboard reports a fine pointer). */
const TOUCH_QUERY = '(any-pointer: coarse)';

const isTouchScreen = (): boolean =>
  (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0) ||
  (typeof window.matchMedia === 'function' && window.matchMedia(TOUCH_QUERY).matches);

/** «05.08.2026» for a stored `2026-08-05`; anything else as it is. */
const displayDate = (value: string): string => (validDate(value) ? dateFullRu(value) : value);

/**
 * Sets the value as the user would, so React's `onChange` of the input runs: through the native
 * setter (React tracks the property) and a bubbling `input` event.
 */
function writeValue(input: HTMLInputElement, value: string): void {
  Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set?.call(input, value);
  input.dispatchEvent(new Event('input', { bubbles: true }));
}

export type CalendarInputProps = InputHTMLAttributes<HTMLInputElement>;

/**
 * A drop-in `<input>`: with `type="date"` a click opens the app's calendar (DatePopover) instead of
 * the browser's; any other type is a plain input. The value contract stays the native one
 * (`YYYY-MM-DD` in `onChange`), so a desktop keyboard still types the date. A picked day is
 * written into the input and then the input is blurred, so fields that commit on blur save it.
 * On a touch screen the input is a read-only text field showing «05.08.2026»: WebKit opens its own
 * picker for any `type="date"` tap, read-only or not (V186).
 */
export const CalendarInput = forwardRef<HTMLInputElement, CalendarInputProps>(function CalendarInput(
  { type, readOnly, disabled, onClick, onKeyDown, ...rest },
  ref,
) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isOpen, setOpen] = useState(false);
  const [isTouch] = useState(isTouchScreen);
  const hasCalendar = type === 'date' && !readOnly && !disabled;
  const isTouchDate = hasCalendar && isTouch;
  const value = typeof rest.value === 'string' ? rest.value : '';
  const setRefs = (element: HTMLInputElement | null) => {
    inputRef.current = element;
    if (typeof ref === 'function') ref(element);
    else if (ref) ref.current = element;
  };

  const pick = (date: string) => {
    const input = inputRef.current;
    setOpen(false);
    if (!input) return;
    input.focus({ preventScroll: true });
    writeValue(input, date);
    window.setTimeout(() => input.blur());
  };
  const handleClick = (event: MouseEvent<HTMLInputElement>) => {
    onClick?.(event);
    if (hasCalendar && !event.defaultPrevented) setOpen(true);
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') setOpen(false);
    onKeyDown?.(event);
  };

  return (
    <>
      <input
        ref={setRefs}
        type={isTouchDate ? 'text' : type}
        {...rest}
        value={isTouchDate ? displayDate(value) : rest.value}
        inputMode={isTouchDate ? 'none' : rest.inputMode}
        disabled={disabled}
        readOnly={readOnly || isTouchDate}
        data-calendar={hasCalendar ? '' : undefined}
        aria-haspopup={hasCalendar ? 'dialog' : undefined}
        aria-expanded={hasCalendar ? isOpen : undefined}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      />
      {hasCalendar && isOpen && (
        <DatePopover
          anchorRef={inputRef}
          value={value}
          canClear={value !== '' && !rest.required}
          onPick={pick}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
});
